import { LOGOUT } from '@plone/volto/constants/ActionTypes';
import {
  ARQUIVAR_MPF_NOTIFICACAO,
  GET_MPF_MINHA_FILA,
  GET_MPF_NOTIFICACOES,
  GET_MPF_NOTIFICACOES_CONTAGEM,
  MARCAR_MPF_NOTIFICACAO,
  MARCAR_TODAS_MPF_NOTIFICACOES,
} from '../../constants';
import type {
  ContagemNotificacoes,
  FiltroNotificacoes,
  ItemFila,
  Notificacao,
} from 'volto-ministerio-publico-federal/helpers/Notificacoes';

export type NotificacoesState = {
  contagem: ContagemNotificacoes;
  /** Já houve ao menos uma contagem com sucesso nesta sessão. */
  contagemCarregada: boolean;
  lista: {
    filtro: FiltroNotificacoes;
    bStart: number;
    items: Notificacao[];
    itemsTotal: number;
    loading: boolean;
    loaded: boolean;
    error: unknown;
  };
  fila: {
    items: ItemFila[];
    loading: boolean;
    loaded: boolean;
    error: unknown;
  };
};

export const initialState: NotificacoesState = {
  contagem: { nao_lidas: 0, acao_necessaria: 0 },
  contagemCarregada: false,
  lista: {
    filtro: 'todas',
    bStart: 0,
    items: [],
    itemsTotal: 0,
    loading: false,
    loaded: false,
    error: null,
  },
  fila: { items: [], loading: false, loaded: false, error: null },
};

const contagemDe = (result: any, atual: ContagemNotificacoes) => ({
  nao_lidas:
    typeof result?.nao_lidas === 'number' ? result.nao_lidas : atual.nao_lidas,
  acao_necessaria:
    typeof result?.acao_necessaria === 'number'
      ? result.acao_necessaria
      : atual.acao_necessaria,
});

/** Ajusta os contadores quando uma notificação muda de lida/não lida. */
const ajustarContagem = (
  contagem: ContagemNotificacoes,
  item: Notificacao | undefined,
  delta: number,
): ContagemNotificacoes => {
  if (!item) return contagem;
  return {
    nao_lidas: Math.max(contagem.nao_lidas + delta, 0),
    acao_necessaria:
      item.categoria === 'acao_necessaria'
        ? Math.max(contagem.acao_necessaria + delta, 0)
        : contagem.acao_necessaria,
  };
};

/** Central de notificações do usuário autenticado (`@notificacoes`). */
export const mpfNotificacoesReducer = (
  state: NotificacoesState = initialState,
  action: any = {},
): NotificacoesState => {
  switch (action.type) {
    case `${GET_MPF_NOTIFICACOES_CONTAGEM}_SUCCESS`:
      return {
        ...state,
        contagem: contagemDe(action.result, state.contagem),
        contagemCarregada: true,
      };

    case `${GET_MPF_NOTIFICACOES}_PENDING`:
      return {
        ...state,
        lista: {
          ...state.lista,
          filtro: action.filtro ?? state.lista.filtro,
          bStart: action.bStart ?? 0,
          loading: true,
          error: null,
        },
      };

    case `${GET_MPF_NOTIFICACOES}_SUCCESS`:
      return {
        ...state,
        contagem: contagemDe(action.result, state.contagem),
        contagemCarregada: true,
        lista: {
          ...state.lista,
          items: action.result?.items ?? [],
          itemsTotal: action.result?.items_total ?? 0,
          loading: false,
          loaded: true,
          error: null,
        },
      };

    case `${GET_MPF_NOTIFICACOES}_FAIL`:
      return {
        ...state,
        lista: { ...state.lista, loading: false, error: action.error },
      };

    case `${MARCAR_MPF_NOTIFICACAO}_SUCCESS`: {
      const item = state.lista.items.find((n) => n.id === action.id);
      if (!item || item.lida === action.lida) return state;
      return {
        ...state,
        contagem: ajustarContagem(state.contagem, item, action.lida ? -1 : 1),
        lista: {
          ...state.lista,
          items: state.lista.items.map((n) =>
            n.id === action.id ? { ...n, lida: action.lida } : n,
          ),
        },
      };
    }

    case `${MARCAR_TODAS_MPF_NOTIFICACOES}_SUCCESS`:
      return {
        ...state,
        contagem: { nao_lidas: 0, acao_necessaria: 0 },
        lista: {
          ...state.lista,
          items:
            state.lista.filtro === 'todas'
              ? state.lista.items.map((n) => ({ ...n, lida: true }))
              : [],
          itemsTotal:
            state.lista.filtro === 'todas' ? state.lista.itemsTotal : 0,
        },
      };

    case `${ARQUIVAR_MPF_NOTIFICACAO}_SUCCESS`: {
      const item = state.lista.items.find((n) => n.id === action.id);
      return {
        ...state,
        contagem:
          item && !item.lida
            ? ajustarContagem(state.contagem, item, -1)
            : state.contagem,
        lista: {
          ...state.lista,
          items: state.lista.items.filter((n) => n.id !== action.id),
          itemsTotal: Math.max(state.lista.itemsTotal - (item ? 1 : 0), 0),
        },
      };
    }

    case `${GET_MPF_MINHA_FILA}_PENDING`:
      return { ...state, fila: { ...state.fila, loading: true, error: null } };

    case `${GET_MPF_MINHA_FILA}_SUCCESS`:
      return {
        ...state,
        fila: {
          items: action.result?.items ?? [],
          loading: false,
          loaded: true,
          error: null,
        },
      };

    case `${GET_MPF_MINHA_FILA}_FAIL`:
      return {
        ...state,
        fila: { ...state.fila, loading: false, error: action.error },
      };

    // Outra pessoa pode entrar no mesmo navegador: nada do anterior fica.
    case LOGOUT:
    case `${LOGOUT}_SUCCESS`:
      return initialState;

    default:
      return state;
  }
};

export default mpfNotificacoesReducer;
